import React, { useMemo, useState, useEffect } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import {
  FaUsers,
  FaBuilding,
  FaHardHat,
  FaClock,
  FaLink,
  FaCalendarCheck,
  FaTable,
  FaSearch,
  FaProjectDiagram,
  FaQuestionCircle,
  FaGasPump,
  FaTruckMonster,
  FaTools,
  FaOilCan,
  FaMobileAlt,
  FaKey,
  FaMoneyBillWave,
  FaShoppingCart,
  FaChartBar,
  FaExclamationTriangle,
  FaPhoneAlt,
  FaLaptop,
  FaReceipt,
  FaCalculator,
  FaWifi
} from 'react-icons/fa';
import { Modal, Button, Badge, Form, Alert, Spinner } from 'react-bootstrap';
import axios from 'axios';
import '../styles/HomePage.css';
import { checkPasswordStrength, PASSWORD_RULE_MESSAGE } from '../utils/passwordPolicy';

const HomePage = ({ user }) => {
  const navigate = useNavigate();

  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  const CACHE_KEY = 'homepage_kpis_cache';

  const todayStr = new Date().toISOString().split('T')[0];
  const yearStart = new Date().getFullYear() + '-01-01';

  const [dateDebut, setDateDebut] = useState(yearStart);
  const [dateFin,   setDateFin]   = useState(todayStr);

  const [kpis, setKpis]           = useState(() => {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [kpisRefreshing, setKpisRefreshing] = useState(false);

  const _privs = Array.isArray(user?.privileges) ? user.privileges : [];
  const hasDecisionnel     = user?.role === 'admin' || user?.role === 'resp_commercial' || _privs.includes('decisionnel');
  const hasDashboard       = user?.role === 'admin' || _privs.includes('dashboard');
  const hasArticlesSuivis  = user?.role === 'admin' || _privs.includes('articles-suivis');
  const hasBdcSuivis       = user?.role === 'admin' || _privs.includes('bdc-suivis');

  const fetchKpis = React.useCallback((debut, fin) => {
    const role = user?.role;
    const hasAnyAccess = ['admin', 'responsable_budget', 'achat'].includes(role)
      || hasDecisionnel || hasDashboard || hasArticlesSuivis || hasBdcSuivis;
    if (!hasAnyAccess) return;
    setKpisRefreshing(true);

    const calls = [];
    if (role === 'admin' || role === 'achat' || hasDashboard)
      calls.push(api.get(`/dashboard/summary?date_debut=${debut}&date_fin=${fin}`));
    else calls.push(Promise.resolve({ data: null }));

    calls.push(api.get(`/dashboard/budget-realisation?date_debut=${debut}&date_fin=${fin}`));
    calls.push(api.get('/articles-suivis').catch(() => ({ data: [] })));
    calls.push(api.get(`/bdc-suivis?date_debut=${debut}&date_fin=${fin}`).catch(() => ({ data: { items: [] } })));
    calls.push(hasDecisionnel
      ? api.get(`/decisionnel/kpi?debut=${debut}&fin=${fin}`).catch(() => ({ data: null }))
      : Promise.resolve({ data: null })
    );

    Promise.all(calls).then(([s, b, art, bdc, dec]) => {
      const fresh = {
        summary:      s.data,
        budget:       b.data,
        articles:     Array.isArray(art.data) ? art.data : (art.data?.items || []),
        bdcSuivis:    Array.isArray(bdc.data) ? bdc.data : (bdc.data?.items || bdc.data?.data || []),
        decisionnel:  dec.data,
        cachedAt: Date.now()
      };
      setKpis(fresh);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(fresh)); } catch {}
    }).catch(() => {}).finally(() => setKpisRefreshing(false));
  }, [user?.role, hasDecisionnel, hasDashboard, hasArticlesSuivis, hasBdcSuivis]);

  // Auto-refresh avec debounce 700ms quand les dates changent
  useEffect(() => {
    const t = setTimeout(() => fetchKpis(dateDebut, dateFin), 700);
    return () => clearTimeout(t);
  }, [user?.role, dateDebut, dateFin, fetchKpis]);

  const fmtM = (v) => {
    const n = Number(v || 0);
    if (n >= 1e6) return (n / 1e6).toFixed(1) + ' M';
    if (n >= 1e3) return Math.round(n / 1e3) + ' K';
    return String(Math.round(n));
  };

  const roleLabel = useMemo(() => {
    const r = user?.role;
    if (r === 'admin') return { text: 'Admin', variant: 'danger' };
    if (r === 'responsable') return { text: 'Responsable', variant: 'warning' };
    if (r === 'pointeur') return { text: 'Pointeur', variant: 'success' };
    if (r === 'responsable_budget') return { text: 'Resp. Budget', variant: 'secondary' };
    if (r === 'achat') return { text: 'Achat', variant: 'info' };
    if (r === 'resp_commercial') return { text: 'Resp. Commercial', variant: 'danger' };
    return { text: r || 'Utilisateur', variant: 'secondary' };
  }, [user]);

  const categoryStyles = {
    rapports: {
      title: '📊 Rapports & Suivi',
      accent: '#6d4e36',
      bg: 'linear-gradient(135deg, rgba(109,78,54,0.14), rgba(245,245,219,0.9))'
    },
    pointage: {
      title: '🕒 Pointage & Présence',
      accent: '#2e7d32',
      bg: 'linear-gradient(135deg, rgba(46,125,50,0.14), rgba(245,245,219,0.9))'
    },
    materiel: {
      title: '🚜 Matériels & Carburant',
      accent: '#1565c0',
      bg: 'linear-gradient(135deg, rgba(21,101,192,0.14), rgba(245,245,219,0.9))'
    },
    admin: {
      title: '🛡️ Administration',
      accent: '#455a64',
      bg: 'linear-gradient(135deg, rgba(69,90,100,0.14), rgba(245,245,219,0.9))'
    },
    aide: {
      title: '❓ Assistance',
      accent: '#4e7b6d',
      bg: 'linear-gradient(135deg, rgba(78,123,109,0.14), rgba(245,245,219,0.9))'
    }
  };

  const allCards = [
    {
      title: '📊 Tableau de bord',
      icon: <FaChartBar size={28} />,
      path: '/dashboard',
      description: 'Santé du parc, main-d\'œuvre, matériel & gasoil, entretien, citernes — listes et fiches détaillées',
      roles: ['admin', 'responsable', 'responsable_budget', 'achat'],
      privilegeKey: 'dashboard',
      category: 'rapports'
    },
    {
      title: '🏢 Shems Dashboard',
      icon: <FaChartBar size={28} />,
      path: '/decisionnel',
      description: 'Performance commerciale : ventes, recouvrement, budget vs réalisé',
      roles: ['admin', 'resp_commercial'],
      privilegeKey: 'decisionnel',
      category: 'rapports'
    },
    {
      title: '📰 Point du jour',
      icon: <FaChartBar size={28} />,
      path: '/point-jour?vue=commercial',
      description: 'Résumé quotidien direction : hier, mois vs objectif, alertes — rapports partageables',
      roles: ['admin', 'resp_commercial'],
      privilegeKey: 'decisionnel',
      category: 'rapports'
    },
    {
      title: '🛠 Point du jour technique',
      icon: <FaChartBar size={28} />,
      path: '/point-jour?vue=technique',
      description: 'Résumé quotidien du dashboard : score, pointages, matériel, gasoil, vidanges — rapports partageables',
      roles: ['admin', 'responsable', 'responsable_budget', 'achat'],
      privilegeKey: 'dashboard',
      category: 'rapports'
    },
    {
      title: '💼 Cienco Dashboard 2025',
      icon: <FaMoneyBillWave size={28} />,
      path: '/budget-realisation',
      description: 'BDC, BR, factures achat/vente et charges par projet',
      roles: ['admin', 'responsable_budget', 'achat'],
      privilegeKey: 'budget-realisation',
      category: 'rapports'
    },
    {
      title: '⚠️ Articles Critiques',
      icon: <FaExclamationTriangle size={28} />,
      path: '/articles-suivis',
      description: 'Suivi stock, seuils critiques, BDC en attente et factures à payer',
      roles: ['admin', 'responsable_budget', 'achat'],
      privilegeKey: 'articles-suivis',
      category: 'rapports'
    },
    {
      title: '📦 Suivi BDC',
      icon: <FaShoppingCart size={28} />,
      path: '/bdc-suivis',
      description: 'Suivre l\'avancement des bons de commande et leur réception',
      roles: ['admin', 'responsable_budget', 'achat'],
      privilegeKey: 'bdc-suivis',
      category: 'rapports'
    },
    {
      title: '🧮 Outils Comptabilité',
      icon: <FaCalculator size={28} />,
      path: '/outils-comptabilite',
      description: 'Espace dédié aux différents traitements et utilitaires comptables',
      roles: ['admin', 'responsable_budget'],
      privilegeKey: 'comptabilite',
      category: 'rapports'
    },
    {
      title: '👥 Utilisateurs',
      icon: <FaUsers size={28} />,
      path: '/users',
      description: "Gérer les comptes et rôles d'accès",
      roles: ['admin'],
      category: 'admin'
    },
    {
      title: '📱 Devices Utilisateurs',
      icon: <FaMobileAlt size={28} />,
      path: '/user-devices',
      description: 'Voir, bloquer et débloquer les appareils liés aux comptes',
      roles: ['admin'],
      category: 'admin'
    },
    {
      title: '🏗️ Chantiers',
      icon: <FaBuilding size={28} />,
      path: '/chantiers',
      description: 'Lister et gérer les chantiers',
      roles: ['admin'],
      category: 'admin'
    },
    {
      title: '🔗 Affectations',
      icon: <FaLink size={28} />,
      path: '/affectations',
      description: 'Affecter les utilisateurs aux chantiers',
      roles: ['admin'],
      category: 'admin'
    },
    {
      title: '🧩 Accès Départements',
      icon: <FaProjectDiagram size={28} />,
      path: '/affectations-departements',
      description: 'Gérer les accès des utilisateurs aux départements',
      roles: ['admin'],
      category: 'admin'
    },
    {
      title: '📊 KPI Performance',
      icon: <FaChartBar size={28} />,
      path: '/kpi-admin',
      description: 'Gérer les KPIs et la config performance par utilisateur',
      roles: ['admin'],
      category: 'admin'
    },
    {
      title: '🏢 Coût par tranche',
      icon: <FaTable size={28} />,
      path: '/bien-tranches',
      description: 'Analyse des tranches immobilières — superficie, nb biens, coût/m²',
      roles: ['admin'],
      category: 'admin'
    },
    {
      title: '📱 Téléphones',
      icon: <FaPhoneAlt size={28} />,
      path: '/telephones',
      description: 'Gestion des numéros téléphones par société et opérateur',
      roles: ['admin', 'responsable'],
      privilegeKey: 'telephones',
      category: 'admin'
    },
    {
      title: '💻 Parc Informatique',
      icon: <FaLaptop size={28} />,
      path: '/parc-informatique',
      description: 'Inventaire appareils IT — téléphones, tablettes, laptops, imprimantes…',
      roles: ['admin', 'responsable'],
      privilegeKey: 'parc-informatique',
      category: 'admin'
    },
    {
      title: '📡 Abonnements',
      icon: <FaWifi size={28} />,
      path: '/abonnements',
      description: 'Gérer les abonnements (WiFi, IP, streaming…) et suivre les échéances',
      roles: ['admin', 'responsable'],
      privilegeKey: 'abonnement',
      category: 'admin'
    },

    {
      title: '👷‍♂️ Ouvriers',
      icon: <FaHardHat size={28} />,
      path: '/ouvriers',
      description: 'Fiches ouvriers et qualifications',
      roles: ['admin', 'responsable'],
      privilegeKey: 'ouvriers',
      category: 'pointage'
    },
    {
      title: '🕒 Pointages',
      icon: <FaClock size={28} />,
      path: '/pointages',
      description: 'Historique de présence et heures',
      roles: ['admin', 'responsable', 'pointeur'],
      privilegeKey: 'pointages',
      category: 'pointage'
    },
    {
      title: '🗓️ Pointage Journalier',
      icon: <FaCalendarCheck size={28} />,
      path: '/pointage-journalier',
      description: 'Effectuer le pointage jour par jour',
      roles: ['admin', 'responsable', 'pointeur'],
      privilegeKey: 'pointage-journalier',
      category: 'pointage'
    },
    {
      title: '📈 Résumé Pointage',
      icon: <FaTable size={28} />,
      path: '/resume-pointage',
      description: 'Vue croisée des pointages par ouvrier et chantier',
      roles: ['admin', 'responsable', 'pointeur'],
      privilegeKey: 'resume-pointage',
      category: 'pointage'
    },
    {
      title: '📊 Rapport Chantier',
      icon: <FaSearch size={28} />,
      path: '/rapport-chantier',
      description: "Nombre d'ouvriers & paie estimée par chantier",
      roles: ['admin', 'responsable'],
      privilegeKey: 'rapport-chantier',
      category: 'pointage'
    },
    {
      title: '💰 Paie Mois',
      icon: <FaMoneyBillWave size={28} />,
      path: '/paie-mois',
      description: 'Synthèse paie mensuelle par ouvrier sur période 21 → 20',
      roles: ['admin', 'responsable'],
      privilegeKey: 'paie-mois',
      category: 'pointage'
    },

    {
      title: '🚜 Matériels',
      icon: <FaTruckMonster size={28} />,
      path: '/materiels',
      description: 'Gérer les engins et véhicules',
      roles: ['admin', 'responsable'],
      privilegeKey: 'materiels',
      category: 'materiel'
    },
    {
      title: '⛽ Pointage Matériel',
      icon: <FaGasPump size={28} />,
      path: '/pointage-materiels',
      description: 'Saisir les heures et interventions',
      roles: ['admin', 'responsable', 'pointeur'],
      privilegeKey: 'pointage-materiels',
      category: 'materiel'
    },
    {
      title: '📋 Pointage Matériel (Liste)',
      icon: <FaTable size={28} />,
      path: '/pointage-materiels-liste',
      description: 'Liste, filtres, export & modification',
      roles: ['admin', 'responsable', 'pointeur'],
      privilegeKey: 'pointage-materiels-liste',
      category: 'materiel'
    },
    {
      title: '📋 Registre STC',
      icon: <FaReceipt size={28} />,
      path: '/stc',
      description: 'Solde de Tout Compte · registre, montants et export',
      roles: ['admin', 'responsable'],
      privilegeKey: 'stc',
      category: 'pointage'
    },
    {
      title: '🛢️ Vidanges',
      icon: <FaOilCan size={28} />,
      path: '/vidanges',
      description: 'Suivi alertes + historique + enregistrer vidange',
      roles: ['admin', 'responsable', 'pointeur'],
      privilegeKey: 'vidanges',
      category: 'materiel'
    },
    {
      title: '🛢️ Citernes Gasoil',
      icon: <FaGasPump size={28} />,
      path: '/citernes-gasoil',
      description: 'Gestion des stocks, alimentation & historique',
      roles: ['admin', 'responsable'],
      privilegeKey: 'citernes-gasoil',
      category: 'materiel'
    },
    {
      title: '🛠️ BDC → Réparations',
      icon: <FaTools size={28} />,
      path: '/bdc-reparations',
      description: 'Charger un BDC, sélectionner des lignes et valider en réparation',
      roles: ['admin', 'responsable'],
      privilegeKey: 'bdc-reparations',
      category: 'materiel'
    },
    {
      title: '🔧 Matériels Réparations',
      icon: <FaTools size={28} />,
      path: '/materiels/reparations',
      description: 'Historique et suivi des réparations par matériel',
      roles: ['admin', 'responsable'],
      privilegeKey: 'materiels-reparations',
      category: 'materiel'
    },

    {
      title: '❓ Aide / Vidéos',
      icon: <FaQuestionCircle size={28} />,
      description: 'Didacticiels vidéos et assistance',
      roles: ['admin', 'responsable', 'responsable_budget', 'achat', 'pointeur'],
      category: 'aide',
      onClick: () => setShowHelpModal(true)
    }
  ];

  const visibleCards = useMemo(() => {
    const role = user?.role;
    const privs = user?.privileges;
    const hasPrivileges = Array.isArray(privs) && privs.length > 0;

    return allCards.filter((c) => {
      if (role === 'admin') return true;
      // Si des privilèges sont définis et que la card en a une clé → le privilège prime sur le rôle
      if (hasPrivileges && c.privilegeKey) return privs.includes(c.privilegeKey);
      // Sinon → basé sur le rôle
      return (c.roles || []).includes(role);
    });
  }, [user]);

  const grouped = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const g = { rapports: [], pointage: [], materiel: [], admin: [], aide: [] };
    visibleCards.forEach((c) => {
      if (q && !c.title.toLowerCase().includes(q) && !(c.description || '').toLowerCase().includes(q)) return;
      const cat = c.category || 'pointage';
      if (!g[cat]) g[cat] = [];
      g[cat].push(c);
    });
    return g;
  }, [visibleCards, searchQuery]);

  const handleCardClick = (card) => {
    if (card.disabled) return;
    if (card.onClick) return card.onClick();
    if (card.path) return navigate(card.path);
  };

  const resetPasswordModalState = () => {
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordError('');
    setPasswordSuccess('');
    setPasswordLoading(false);
  };

  const openPasswordModal = () => {
    resetPasswordModalState();
    setShowPasswordModal(true);
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    resetPasswordModalState();
  };

  const handlePasswordFormChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('⚠️ Merci de remplir tous les champs.');
      return;
    }

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('⚠️ La confirmation du mot de passe ne correspond pas.');
      return;
    }

    const strengthError = checkPasswordStrength(passwordForm.newPassword, user?.login);
    if (strengthError) {
      setPasswordError(`⚠️ ${strengthError}`);
      return;
    }

    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setPasswordError('⚠️ Le nouveau mot de passe doit être différent de l’ancien.');
      return;
    }

    try {
      setPasswordLoading(true);

      const { data } = await axios.post('/auth/change-password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        confirmPassword: passwordForm.confirmPassword
      });

      setPasswordSuccess(data?.message || '✅ Mot de passe modifié avec succès.');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      setTimeout(() => {
        closePasswordModal();
      }, 1200);
    } catch (error) {
      console.error('❌ Erreur changement mot de passe :', error);
      setPasswordError(
        error.response?.data?.message || 'Erreur lors du changement du mot de passe'
      );
    } finally {
      setPasswordLoading(false);
    }
  };

  const renderSection = (key) => {
    const section = categoryStyles[key];
    const cards = grouped[key] || [];
    if (!cards.length) return null;

    return (
      <div className="home-section-card mb-4" key={key}>
        <div className="home-section-header">
          <div className="home-section-title">
            <span className="home-section-dot" style={{ backgroundColor: section.accent }} />
            <span>{section.title}</span>
          </div>
          <span className="home-section-count-badge" style={{ background: section.accent }}>{cards.length}</span>
        </div>

        <div className="row mt-2 g-3">
          {cards.map((card, idx) => (
            <div className="col-6 col-md-4 col-lg-3" key={`${key}-${idx}`}>
              <div
                className={`home-tile ${card.disabled ? 'home-tile-disabled' : ''}`}
                onClick={() => handleCardClick(card)}
                role="button"
                tabIndex={0}
                style={{ borderTopColor: section.accent, background: section.accent + '0f' }}
              >
                <div className="home-tile-icon" style={{ background: section.accent + '22', color: section.accent }}>
                  {card.icon}
                </div>
                <div className="home-tile-body">
                  <div className="home-tile-title">{card.title}</div>
                  <div className="home-tile-desc">{card.description}</div>
                  {card.disabled && (
                    <div className="home-tile-soon">
                      <Badge bg="dark">Bientôt</Badge>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="container mt-4 home-page">
      <div className="home-header mb-3">
        <div className="home-header-left">
          <div className="home-greeting">
            <span className="home-avatar">
              {(user?.name || 'U').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
            </span>
            Bonjour, {user?.name || 'Utilisateur'}
          </div>
          <div className="home-subtitle">
            <Badge bg={roleLabel.variant} className="me-2">{roleLabel.text}</Badge>
            <span className="home-date">{new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
          </div>
        </div>

        <div className="home-header-actions d-flex gap-2 flex-wrap">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={openPasswordModal}
            title="Changer mon mot de passe"
          >
            <FaKey className="me-1" />
            Mot de passe
          </Button>

          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowHelpModal(true)}
            title="Aide & Vidéos"
          >
            <FaQuestionCircle className="me-1" />
            Aide
          </Button>
        </div>
      </div>

      <div className="home-dashboard">
        <div className="home-dashboard-header">
          <div className="home-dashboard-title">
            Accès rapide
          </div>
          <div className="home-search-wrap">
            <FaSearch className="home-search-icon" />
            <input
              className="home-search-input"
              type="text"
              placeholder="Rechercher un module…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="home-search-clear" onClick={() => setSearchQuery('')}>✕</button>
            )}
          </div>
        </div>

        {(['admin', 'responsable_budget', 'achat'].includes(user?.role) || hasDecisionnel || hasDashboard || hasArticlesSuivis || hasBdcSuivis) && (() => {
          const s = kpis?.summary;
          const b = kpis?.budget;
          const t = b?.totaux || {};
          const articles   = kpis?.articles  || [];
          const bdcSuivis  = kpis?.bdcSuivis || [];

          const nbArticlesCritiques = articles.filter(a => a.etat === 'critique').length;
          const nbArticlesAlertes   = articles.filter(a => a.etat === 'alerte').length;

          const nbBdcEnAttente  = bdcSuivis.filter(b => b.statut === 'en_attente').length;
          const nbBdcPartiel    = bdcSuivis.filter(b => b.statut === 'partiel').length;
          const nbBdcNonFacture = bdcSuivis.filter(b => b.statut_paiement === 'non_facture').length;
          const nbBdcNonPaye    = bdcSuivis.filter(b => b.statut_paiement === 'non_paye' || b.statut_paiement === 'partiel').length;

          const pct = (part, total) => total > 0 ? Math.round((part / total) * 100) : 0;
          const pctRec      = b ? pct(t.receptionnes_ttc,          t.total_commandes_ttc)           : null;
          const pctBrFact   = b ? pct(t.facturees_ttc,             t.total_reception_ttc)            : null;
          const pctPayeAch  = b ? pct(t.factures_achat_payees_ttc, t.total_factures_achat_ttc)       : null;
          const pctPayeVte  = b ? pct(t.ca_vente_paye_ttc,         t.total_ca_vente_ttc)             : null;
          const pctBrVte    = (b && t.total_reception_br_fv_ttc > 0) ? pct(t.facturees_br_vente_ttc, t.total_reception_br_fv_ttc) : null;

          const echeances      = b?.factures_echeance || [];
          const eDepasses      = echeances.filter(e => e.statut_echeance === 'depasse' && e.ResteAPayer > 0);
          const eProches       = echeances.filter(e => e.statut_echeance === 'proche'  && e.ResteAPayer > 0);
          const montantDepasse = eDepasses.reduce((a, e) => a + (e.ResteAPayer || 0), 0);
          const montantProche  = eProches.reduce((a, e) => a + (e.ResteAPayer || 0), 0);

          const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '';
          const periode1 = s?.periode ? `${fmtDate(s.periode.start)} → ${fmtDate(s.periode.end)}` : null;
          const periode2 = b?.periode ? `${fmtDate(b.periode.date_debut)} → ${fmtDate(b.periode.date_fin)}` : null;

          const cachedAtLabel = kpis?.cachedAt
            ? new Date(kpis.cachedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
            : null;

          const cardBase = {
            position: 'relative', borderRadius: 14,
            padding: '1rem 1.2rem', background: '#fff',
            border: '1.5px solid rgba(109,78,54,0.18)',
            borderTop: '4px solid #6d4e36', cursor: 'pointer',
            boxShadow: '0 2px 10px rgba(109,78,54,0.08)',
            transition: 'transform 0.17s ease, box-shadow 0.17s ease',
            overflow: 'hidden'
          };

          const kpiPill = (label, value, color = '#6d4e36') => (
            <div key={label} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              background: `${color}12`, border: `1px solid ${color}28`,
              borderRadius: 20, padding: '2px 10px', fontSize: '0.73rem',
              fontWeight: 600, color, whiteSpace: 'nowrap'
            }}>
              <span style={{ fontWeight: 800 }}>{value}</span>
              <span style={{ fontWeight: 400, opacity: 0.75 }}>{label}</span>
            </div>
          );

          const kpiSection = (label, children) => (
            <div>
              <div style={{ fontSize: '0.6rem', fontWeight: 700, color: '#bbb', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.6px' }}>{label}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>{children}</div>
            </div>
          );

          const kpiBar = (label, pct, color = '#6d4e36') => (
            <div style={{ marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', marginBottom: 3 }}>
                <span style={{ color: '#888' }}>{label}</span>
                <span style={{ fontWeight: 700, color }}>{pct}%</span>
              </div>
              <div style={{ height: 5, background: 'rgba(109,78,54,0.08)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: color, borderRadius: 4, transition: 'width 0.5s ease' }} />
              </div>
            </div>
          );

          const kpiBig = (value, label, color = '#6d4e36') => (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontSize: '1.6rem', fontWeight: 900, color, lineHeight: 1 }}>{value}</span>
              <span style={{ fontSize: '0.7rem', color: '#999' }}>{label}</span>
            </div>
          );

          const hoverOn = (e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 14px 32px rgba(0,0,0,0.13)';
          };
          const hoverOff = (e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 10px rgba(109,78,54,0.08)';
          };

          const cardHeader = (icon, title, subtitle, color = '#6d4e36') => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{
                background: color,
                borderRadius: 10, width: 36, height: 36, flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 3px 10px ${color}55`
              }}>
                {icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: '0.88rem', color, lineHeight: 1.2 }}>{title}</div>
                <div style={{ fontSize: '0.67rem', color: '#aaa', marginTop: 1 }}>{subtitle}</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 }}>
                <span style={{ fontSize: '0.67rem', color, opacity: 0.45, fontWeight: 700 }}>
                  {kpisRefreshing ? '↻' : 'Voir →'}
                </span>
                {cachedAtLabel && (
                  <span style={{ fontSize: '0.62rem', color: '#bbb', whiteSpace: 'nowrap' }}>
                    {kpisRefreshing ? 'actualisation…' : `màj ${cachedAtLabel}`}
                  </span>
                )}
              </div>
            </div>
          );

          const dec = kpis?.decisionnel;

          return (
            <>
            {/* ── Sélecteur de période ── */}
            <div className="home-period-bar" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
              {/* Bloc dates groupé */}
              <div className="home-period-inputs" style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1.5px solid rgba(109,78,54,0.2)', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 4px rgba(109,78,54,0.07)' }}>
                <span style={{ padding: '5px 10px', fontSize: '0.72rem', fontWeight: 700, color: '#9e8878', borderRight: '1px solid rgba(109,78,54,0.12)', whiteSpace: 'nowrap', userSelect: 'none' }}>
                  Période
                </span>
                <input type="date" value={dateDebut} onChange={e => setDateDebut(e.target.value)}
                  style={{ fontSize: '0.8rem', padding: '5px 8px', border: 'none', outline: 'none', color: '#6d4e36', background: 'transparent', cursor: 'pointer' }} />
                <span style={{ color: '#c8b49a', fontSize: '0.75rem', padding: '0 2px', userSelect: 'none' }}>→</span>
                <input type="date" value={dateFin} onChange={e => setDateFin(e.target.value)}
                  style={{ fontSize: '0.8rem', padding: '5px 8px', border: 'none', outline: 'none', color: '#6d4e36', background: 'transparent', cursor: 'pointer' }} />
              </div>

              {/* Bouton actualiser */}
              <button onClick={() => fetchKpis(dateDebut, dateFin)} disabled={kpisRefreshing}
                style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', padding: '5px 12px', borderRadius: 10, border: '1.5px solid rgba(109,78,54,0.25)', background: kpisRefreshing ? '#f5f0e8' : '#6d4e36', color: kpisRefreshing ? '#9e8878' : '#fff', cursor: kpisRefreshing ? 'not-allowed' : 'pointer', fontWeight: 600, transition: 'all 0.15s' }}>
                <span style={{ display: 'inline-block', animation: kpisRefreshing ? 'spin 0.8s linear infinite' : 'none' }}>↻</span>
                {kpisRefreshing ? 'Actualisation…' : 'Actualiser'}
              </button>

              {/* Timestamp màj */}
              {kpis?.cachedAt && !kpisRefreshing && (
                <span style={{ fontSize: '0.67rem', color: '#c8b49a', fontStyle: 'italic' }}>
                  màj {new Date(kpis.cachedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              )}
            </div>

            <div className="home-kpi-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14, marginBottom: '1.5rem' }}>

              {/* ── Card 1 : Shems Dashboard ── */}
              {hasDecisionnel && (
                <div role="button"
                  style={{ ...cardBase, borderTop: '4px solid #2d7a2d', background: '#2d7a2d0f' }}
                  onClick={() => navigate('/decisionnel')}
                  onMouseEnter={hoverOn} onMouseLeave={hoverOff}
                >
                  <img src="/iconshems.png" alt="" style={{ position: 'absolute', bottom: 6, right: 10, width: '4.5rem', opacity: 0.07, pointerEvents: 'none' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <div style={{ borderRadius: 10, width: 36, height: 36, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', boxShadow: '0 3px 10px rgba(45,122,45,0.25)' }}>
                      <img src="/iconshems.png" alt="Shems" style={{ width: 28, height: 28, objectFit: 'contain' }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 800, fontSize: '0.88rem', color: '#2d7a2d', lineHeight: 1.2 }}>Shems Dashboard</div>
                      <div style={{ fontSize: '0.67rem', color: '#aaa', marginTop: 1 }}>{periode1 ? `Période : ${periode1}` : 'Ventes · Recouvrement · Budget vs Réalisé'}</div>
                    </div>
                  </div>

                  {dec ? (() => {
                    const pctColor = (v) => v == null ? '#bbb' : v >= 100 ? '#2e7d32' : v >= 70 ? '#e65100' : '#c62828';
                    const pctArrow = (v) => v == null ? '' : v >= 100 ? ' ↑' : ' ↓';
                    const metricRow = (label, realise, budget, pctVal, color) => (
                      <div key={label} style={{ marginBottom: 6 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                          <span style={{ fontSize: '0.68rem', color: '#888' }}>{label}</span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            {budget > 0 && kpiPill('bdgt', fmtM(budget) + ' MAD', '#bbb')}
                            <span style={{ fontSize: '0.78rem', fontWeight: 800, color }}>{fmtM(realise)} MAD</span>
                          </div>
                        </div>
                        {budget > 0 && (
                          <div style={{ position: 'relative', height: 5, background: 'rgba(109,78,54,0.08)', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${Math.min(pctVal || 0, 100)}%`, background: pctColor(pctVal), borderRadius: 4, transition: 'width 0.5s ease' }} />
                          </div>
                        )}
                        {pctVal != null && (
                          <div style={{ fontSize: '0.66rem', fontWeight: 700, color: pctColor(pctVal), textAlign: 'right', marginTop: 1 }}>
                            {pctVal}%{pctArrow(pctVal)}
                          </div>
                        )}
                      </div>
                    );
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {metricRow('CA Ventes', dec.realisVente, dec.budgetVente, dec.pctVente, '#6d4e36')}
                        {metricRow('Recouvrement', dec.realisRec, dec.budgetRec, dec.pctRec, '#2e7d32')}
                        {metricRow('Passages Notaire', dec.realisPn, dec.budgetPn, dec.pctPn, '#1b5e20')}
                        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 2 }}>
                          {dec.nbComm > 0 && kpiPill('nb comm', Number(dec.nbComm).toLocaleString('fr-FR', { maximumFractionDigits: 1 }), '#6d4e36')}
                          {dec.nbPn   > 0 && kpiPill('nb PN',  dec.nbPn, '#1b5e20')}
                          {dec.encImpaye > 0 && kpiPill('à payer', fmtM(dec.encImpaye) + ' MAD', '#c62828')}
                        </div>
                      </div>
                    );
                  })() : <span style={{ fontSize: '0.72rem', color: '#bbb' }}>{kpisRefreshing ? 'Chargement…' : 'Aucune donnée'}</span>}
                </div>
              )}

              {/* ── Card 2 : Cienco Dashboard ── */}
              {user?.role !== 'resp_commercial' && <div role="button" style={{ ...cardBase, borderTop: '4px solid #f9c029', background: 'rgba(249,192,41,0.07)' }} onClick={() => navigate('/budget-realisation')} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
                <img src="/cienco.ico" alt="" style={{ position: 'absolute', bottom: 6, right: 10, width: '4.5rem', opacity: 0.08, pointerEvents: 'none' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div style={{ borderRadius: 10, width: 36, height: 36, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1a1a1a', boxShadow: '0 3px 10px rgba(249,192,41,0.35)' }}>
                    <img src="/cienco.ico" alt="Cienco" style={{ width: 24, height: 24, objectFit: 'contain' }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: '0.88rem', color: '#1a1a1a', lineHeight: 1.2 }}>Cienco Dashboard</div>
                    <div style={{ fontSize: '0.67rem', color: '#aaa', marginTop: 1 }}>{periode2 ? `Période : ${periode2}` : 'BDC · BR · Factures · Charges'}</div>
                  </div>
                </div>
                {b ? (() => {
                  const sep = <div style={{ height: 1, background: 'rgba(109,78,54,0.07)', margin: '5px 0' }} />;
                  const colorBar = (v) => v >= 75 ? '#2e7d32' : v >= 50 ? '#e65100' : '#c62828';
                  // Ligne compacte : label gauche, montant + pill droite
                  const row = (label, amount, color, pill = null) => (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                      <span style={{ fontSize: '0.68rem', color: '#999' }}>{label}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        {pill}
                        <span style={{ fontSize: '0.78rem', fontWeight: 800, color }}>{amount}</span>
                      </div>
                    </div>
                  );
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>

                      {/* BR */}
                      {t.total_reception_ttc > 0 && <div style={{ marginBottom: 4 }}>
                        {row('Réception BR', fmtM(t.total_reception_ttc) + ' MAD', '#1a1a1a',
                          t.non_facturees_ttc > 0 && kpiPill('à facturer', fmtM(t.non_facturees_ttc), '#e65100'))}
                        {pctBrFact !== null && kpiBar('Facturé achat', pctBrFact, colorBar(pctBrFact))}
                        {pctBrVte  !== null && kpiBar('Facturé vente', pctBrVte,  colorBar(pctBrVte))}
                      </div>}

                      {/* Factures Achat */}
                      {t.total_factures_achat_ttc > 0 && <>{sep}
                        <div style={{ marginBottom: 4 }}>
                          {row('Factures Achat', fmtM(t.total_factures_achat_ttc) + ' MAD', '#6d4e36',
                            t.factures_achat_non_payees > 0 && kpiPill('restant', fmtM(t.factures_achat_non_payees), '#c62828'))}
                          {pctPayeAch !== null && kpiBar('Payé', pctPayeAch, colorBar(pctPayeAch))}
                        </div>
                      </>}

                      {/* CA Vente */}
                      {t.total_ca_vente_ttc > 0 && <>{sep}
                        <div style={{ marginBottom: 4 }}>
                          {row('CA Vente Client', fmtM(t.total_ca_vente_ttc) + ' MAD', '#4e7b6d',
                            t.ca_vente_non_paye_ttc > 0 && kpiPill('non enc.', fmtM(t.ca_vente_non_paye_ttc), '#e65100'))}
                          {pctPayeVte !== null && kpiBar('Encaissé', pctPayeVte, colorBar(pctPayeVte))}
                        </div>
                      </>}

                      {/* Échéances */}
                      {(eDepasses.length > 0 || eProches.length > 0) && <>{sep}
                        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                          {eDepasses.length > 0 && <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#c62828', background: '#c6282810', borderRadius: 10, padding: '1px 7px' }}>🔴 {eDepasses.length} — {fmtM(montantDepasse)} MAD</span>}
                          {eProches.length  > 0 && <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#e65100', background: '#e6510010', borderRadius: 10, padding: '1px 7px' }}>🟡 {eProches.length} — {fmtM(montantProche)} MAD</span>}
                        </div>
                      </>}

                    </div>
                  );
                })() : <span style={{ fontSize: '0.72rem', color: '#bbb' }}>{kpisRefreshing ? 'Chargement…' : 'Aucune donnée'}</span>}
              </div>}

              {/* ── Card 3 : Ouvriers / Engins ── */}
              {(['admin', 'achat'].includes(user?.role) || hasDashboard) && <div role="button" style={{ ...cardBase, borderTop: '4px solid #2e7d32', background: '#2e7d320f' }} onClick={() => navigate('/dashboard')} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
                <FaChartBar style={{ position: 'absolute', bottom: 6, right: 14, fontSize: '4.5rem', color: '#6d4e36', opacity: 0.04, pointerEvents: 'none' }} />
                {cardHeader(<FaChartBar size={16} color="#fff" />, 'Technique Dashboard', periode1 ? `Période : ${periode1}` : 'Paie · Pointages · Matériels · Gasoil', '#2e7d32')}
                {s ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <div>{kpiBig(s.ouvriers?.actifs ?? '—', 'pointés sur période', '#2e7d32')}</div>
                      <div style={{ textAlign: 'right' }}>
                        {kpiPill('total', s.ouvriers?.total, '#6d4e36')}
                        {s.ouvriers?.bloques > 0 && <div style={{ marginTop: 4 }}>{kpiPill('bloqués', s.ouvriers.bloques, '#c62828')}</div>}
                      </div>
                    </div>
                    {kpiSection('Masse salariale', <>
                      {kpiPill('période', fmtM(s.masseSalarialeActifs) + ' MAD', '#2e7d32')}
                      {kpiPill("pointages auj.", s.pointages?.aujourdHui, '#1565c0')}
                    </>)}
                    {kpiSection('Matériels actifs', <>
                      {kpiPill('total', s.materiels?.total, '#6d4e36')}
                      {s.materiels?.bloques > 0 && kpiPill('bloqués', s.materiels.bloques, '#c62828')}
                      {s.materiels?.vidangesEnAlerte > 0 && kpiPill('⚠ vidange', s.materiels.vidangesEnAlerte, '#e65100')}
                    </>)}
                    {kpiSection('Chantiers & Gasoil', <>
                      {kpiPill('actifs', s.chantiers?.actifs, '#4e7b6d')}
                      {s.gasoil?.totalLitres != null && kpiPill('stock', Math.round(s.gasoil.totalLitres).toLocaleString('fr-FR') + ' L', '#1565c0')}
                    </>)}
                  </div>
                ) : <span style={{ fontSize: '0.72rem', color: '#bbb' }}>{kpisRefreshing ? 'Chargement…' : 'Aucune donnée'}</span>}
              </div>}

              {/* ── Card 4 : Articles Critiques ── */}
              {(['admin', 'achat', 'responsable_budget'].includes(user?.role) || hasArticlesSuivis) && (
                <div role="button" style={{ ...cardBase, borderTop: '4px solid #c62828', background: '#c628280f' }} onClick={() => navigate('/articles-suivis')} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
                  <FaExclamationTriangle style={{ position: 'absolute', bottom: 6, right: 14, fontSize: '4.5rem', color: '#c62828', opacity: 0.04, pointerEvents: 'none' }} />
                  {cardHeader(<FaExclamationTriangle size={16} color="#fff" />, 'Articles Critiques', 'Stock · Seuils critiques · Alertes', '#c62828')}
                  {articles.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {/* Big number */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        {kpiBig(nbArticlesCritiques + nbArticlesAlertes || '✅', nbArticlesCritiques + nbArticlesAlertes > 0 ? 'à traiter' : 'tout OK', nbArticlesCritiques > 0 ? '#c62828' : nbArticlesAlertes > 0 ? '#e65100' : '#2e7d32')}
                        {kpiPill('suivis', articles.length, '#6d4e36')}
                      </div>
                      {kpiSection('Détail stock', <>
                        {nbArticlesCritiques > 0 && kpiPill('critique', nbArticlesCritiques, '#c62828')}
                        {nbArticlesAlertes   > 0 && kpiPill('alerte',   nbArticlesAlertes,   '#e65100')}
                        {nbArticlesCritiques === 0 && nbArticlesAlertes === 0 && kpiPill('stocks OK', '✅', '#2e7d32')}
                      </>)}
                      {/* Mini bar */}
                      {articles.length > 0 && (() => {
                        const okCount = articles.length - nbArticlesCritiques - nbArticlesAlertes;
                        const pctOk = pct(okCount, articles.length);
                        return kpiBar('Articles en stock normal', pctOk, '#2e7d32');
                      })()}
                    </div>
                  ) : <span style={{ fontSize: '0.72rem', color: '#bbb' }}>{kpisRefreshing ? 'Chargement…' : 'Aucun article suivi'}</span>}
                </div>
              )}

              {/* ── Card 5 : BDC Critiques ── */}
              {(['admin', 'achat', 'responsable_budget'].includes(user?.role) || hasBdcSuivis) && (
                <div role="button" style={{ ...cardBase, borderTop: '4px solid #4527a0', background: '#4527a00f' }} onClick={() => navigate('/bdc-suivis')} onMouseEnter={hoverOn} onMouseLeave={hoverOff}>
                  <FaShoppingCart style={{ position: 'absolute', bottom: 6, right: 14, fontSize: '4.5rem', color: '#1565c0', opacity: 0.04, pointerEvents: 'none' }} />
                  {cardHeader(<FaShoppingCart size={16} color="#fff" />, 'Suivi BDC', periode2 ? `Période : ${periode2}` : 'Réception · Facturation · Paiement', '#4527a0')}
                  {bdcSuivis.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        {kpiBig(bdcSuivis.length, 'BDC suivis', '#1565c0')}
                        {(nbBdcEnAttente + nbBdcPartiel) > 0
                          ? kpiPill('en cours', nbBdcEnAttente + nbBdcPartiel, '#e65100')
                          : kpiPill('tous reçus', '✅', '#2e7d32')}
                      </div>
                      {kpiSection('Réception', <>
                        {nbBdcEnAttente > 0 && kpiPill('en attente', nbBdcEnAttente, '#c62828')}
                        {nbBdcPartiel   > 0 && kpiPill('partiel',    nbBdcPartiel,   '#e65100')}
                        {nbBdcEnAttente === 0 && nbBdcPartiel === 0 && kpiPill('tout reçu', '✅', '#2e7d32')}
                      </>)}
                      {(() => {
                        const recus = bdcSuivis.filter(b => b.statut_reception === 'complet').length;
                        return kpiBar('Taux réception', pct(recus, bdcSuivis.length), recus / bdcSuivis.length >= 0.75 ? '#2e7d32' : '#e65100');
                      })()}
                      {kpiSection('Paiement', <>
                        {nbBdcNonFacture > 0 && kpiPill('à facturer', nbBdcNonFacture, '#c62828')}
                        {nbBdcNonPaye    > 0 && kpiPill('non/part. payés', nbBdcNonPaye, '#e65100')}
                        {nbBdcNonFacture === 0 && nbBdcNonPaye === 0 && kpiPill('tout payé', '✅', '#2e7d32')}
                      </>)}
                    </div>
                  ) : <span style={{ fontSize: '0.72rem', color: '#bbb' }}>{kpisRefreshing ? 'Chargement…' : 'Aucun BDC suivi'}</span>}
                </div>
              )}

            </div>
            </>
          );
        })()}

        {searchQuery && !Object.values(grouped).some(arr => arr.length > 0) && (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#aaa', fontSize: '0.9rem' }}>
            Aucun module trouvé pour « {searchQuery} »
          </div>
        )}
        {renderSection('rapports')}
        {renderSection('pointage')}
        {renderSection('materiel')}
        {renderSection('admin')}
        {renderSection('aide')}
      </div>

      <Modal show={showHelpModal} onHide={() => setShowHelpModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>📽️ Aide & Vidéos</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-md-6 mb-4">
              <strong>🎬 Présentation de l'application & du chat</strong>
              <video width="100%" controls>
                <source src="/videos/tutorial1.mp4" type="video/mp4" />
                Votre navigateur ne supporte pas la vidéo.
              </video>
            </div>
            <div className="col-md-6 mb-4">
              <strong>🗓️ Tutoriel Pointage Journalier</strong>
              <video width="100%" controls>
                <source src="/videos/tutorial2.mp4" type="video/mp4" />
                Votre navigateur ne supporte pas la vidéo.
              </video>
            </div>
          </div>

          <div className="row">
            <div className="col-md-6 mb-4">
              <strong>🚀 Envoyer un ouvrier vers le chantier TOUT</strong>
              <video width="100%" controls>
                <source src="/videos/tutorial3.mp4" type="video/mp4" />
                Votre navigateur ne supporte pas la vidéo.
              </video>
            </div>
            <div className="col-md-6 mb-4">
              <strong>⛽ Tutoriel Pointage Matériel (journalier)</strong>
              <video width="100%" controls>
                <source src="/videos/tutorial4.mp4" type="video/mp4" />
                Votre navigateur ne supporte pas la vidéo.
              </video>
            </div>
            <div className="col-md-6 mb-4">
              <strong>🛢️ Tutoriel Vidange Matériel</strong>
              <video width="100%" controls className="mt-2 rounded shadow-sm">
                <source src="/videos/tutorial5.mp4" type="video/mp4" />
                Votre navigateur ne supporte pas la vidéo.
              </video>
            </div>
          </div>

          <p className="mt-3">
            📩 Pour toute question, vous pouvez :
          </p>
          <ul>
            <li>📨 utiliser le chat intégré en bas de page</li>
            <li>📱 ou envoyer un message WhatsApp au <strong>06 32 29 50 10</strong></li>
          </ul>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowHelpModal(false)}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showPasswordModal} onHide={closePasswordModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>🔐 Changer mon mot de passe</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {passwordError && <Alert variant="danger">{passwordError}</Alert>}
          {passwordSuccess && <Alert variant="success">{passwordSuccess}</Alert>}

          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Mot de passe actuel</Form.Label>
              <Form.Control
                type="password"
                name="currentPassword"
                value={passwordForm.currentPassword}
                onChange={handlePasswordFormChange}
                placeholder="Entrez votre mot de passe actuel"
                autoComplete="current-password"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Nouveau mot de passe</Form.Label>
              <Form.Control
                type="password"
                name="newPassword"
                value={passwordForm.newPassword}
                onChange={handlePasswordFormChange}
                placeholder="Entrez le nouveau mot de passe"
                autoComplete="new-password"
              />
              <Form.Text muted>{PASSWORD_RULE_MESSAGE}</Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Confirmation du nouveau mot de passe</Form.Label>
              <Form.Control
                type="password"
                name="confirmPassword"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordFormChange}
                placeholder="Confirmez le nouveau mot de passe"
                autoComplete="new-password"
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closePasswordModal} disabled={passwordLoading}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleChangePassword} disabled={passwordLoading}>
            {passwordLoading ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Enregistrement...
              </>
            ) : (
              <>
                <FaKey className="me-2" />
                Enregistrer
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default HomePage;